/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import classnames from 'classnames';
import { INavigationMenuProps, ICmsNavItemsData } from '../definition-extensions/navigation-menu.ext.props.autogenerated';
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Drawer,
    ICollapseProps,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick,
    IDrawerState
} from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IImageData } from '@msdyn365-commerce/core';
import { getCategoryImage, INavigationState, NavMenuConstants } from './components/navigation-menu-utilities';

import { trackClickMenu } from '../utilities/datalayer';

type MenuSearchResult = {
    item: IMenuItemData;
    parents: string[];
} | null;

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps & INavigationMenuProps<{}>, INavigationState> {
    private currentLevel: number = 0;

    private readonly _positionInSetOffset: number = 1;
    private readonly escapeKey: number = NavMenuConstants.escapeKey;
    private readonly menuNode: React.RefObject<HTMLUListElement>;

    private readonly telemetryContent: ITelemetryContent;
    private readonly promotionalImage: IImageData[] = [];
    private readonly payLoad: IPayLoad;

    private readonly supportedLevel: number = NavMenuConstants.four;

    constructor(props: INavigationMenuViewProps & INavigationMenuProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
        this.state = { activeMenu: undefined, mobileViewLabelText: '', parentMenu: undefined, drawerKeyValue: {} };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this._escFunction = this._escFunction.bind(this);
        this._layout2022closeSubmenu = this._layout2022closeSubmenu.bind(this);
        this._layout2022escFunction = this._layout2022escFunction.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
        const { menuItemData } = this.props;
        if (ArrayExtensions.hasElements(menuItemData) && ArrayExtensions.hasElements(menuItemData[0].subMenu)) {
            const imagesource = menuItemData[0].subMenu[0].imageSource ? menuItemData[0].subMenu[0].imageSource : '';
            const linkText = menuItemData[0].subMenu[0].linkText ? menuItemData[0].subMenu[0].linkText : '';
            this.promotionalImage = [{ src: imagesource, altText: linkText }];

            // Read category and promotional image in one array
            if (ArrayExtensions.hasElements(menuItemData[0].subMenu[0].promotionalContent)) {
                menuItemData[0].subMenu[0].promotionalContent.map(item => {
                    const imageSource = item.image.src;
                    this.promotionalImage.push({
                        src: imageSource,
                        altText: item.text,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                    return null;
                });
            }
        }
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            //FIXME change if
            if (this.props.config.layout2022 === undefined || !this.props.config.layout2022) {
                document.body && document.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
                document.body && document.body.addEventListener('mousedown', this._handleClickOutside);
            } else {
                document.body && document.addEventListener('keydown', (this._layout2022escFunction as unknown) as EventListener, false);
                document.body && document.body.addEventListener('mousedown', this._layout2022handleClickOutside);
            }
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            //FIXME change if
            if (this.props.config.layout2022 === undefined || !this.props.config.layout2022) {
                document.body && document.removeEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
                document.body && document.body.removeEventListener('mousedown', this._handleClickOutside);
            } else {
                document.body && document.removeEventListener('keydown', (this._layout2022escFunction as unknown) as EventListener, false);
                document.body && document.body.removeEventListener('mousedown', this._layout2022handleClickOutside);
            }
        }
    }

    public render(): JSX.Element | null {
        const { isMobileView } = this.props;

        this.currentLevel = 1;
        return <>{isMobileView ? this._renderMobileMenu() : this._renderDesktopMenu()}</>;
    }

    /**
     * Method to render mobile menu.
     * @returns Jsx element.
     */
    private _renderMobileMenu(): JSX.Element {
        const { isMobileView, menuItemData, Navigation } = this.props;
        return (
            <Module {...Navigation} className={classnames(Navigation.className, isMobileView ? 'mobile-view' : 'desktop-view')}>
                {menuItemData.map((menuItem: IMenuItemData, posinset: number) => {
                    //FIXME invert the condition
                    if (this.props.config.layout2022 === undefined || !this.props.config.layout2022)
                        return this._renderDrawerMenu(menuItem);
                    else return this._layout2022renderDrawerMenu(menuItem, menuItemData.length, posinset + this._positionInSetOffset);
                })}
            </Module>
        );
    }

    /**
     * Method to render desktop menu.
     * @returns Jsx element.
     */
    private _renderDesktopMenu(): JSX.Element {
        const { MenuList, Navigation } = this.props;
        //FIXME invert condition
        if (this.props.config.layout2022 === undefined || !this.props.config.layout2022)
            return (
                <Module {...Navigation} className={classnames(Navigation.className)}>
                    <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                        {this._renderDisplay()}
                    </Node>
                </Module>
            );
        else
            return (
                <Module {...Navigation} className={classnames(Navigation.className)}>
                    <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                        {this._layout2022renderDisplay()}
                    </Node>
                </Module>
            );
    }

    private _renderDrawerMenu(menuItem: IMenuItemData): JSX.Element | null {
        if (menuItem && menuItem.subMenu && menuItem.subMenu.length > 0) {
            return this._renderDrawer(menuItem);
        } else {
            return this._renderLinkMenuItem(menuItem);
        }
    }

    private _renderDrawer(menuItem: IMenuItemData): JSX.Element | null {
        const toggleButtonText = menuItem.linkText;
        return (
            <Drawer
                key={menuItem.id}
                className={'ms-nav__drawer'}
                openGlyph='ms-nav__drawer-open'
                closeGlyph='ms-nav__drawer-close'
                glyphPlacement='end'
                toggleButtonText={this._renderLinkText(toggleButtonText)}
            >
                <div>
                    {menuItem.subMenu!.map((menuSubItem: IMenuItemData) => {
                        return this._renderDrawerLink(menuSubItem);
                    })}
                </div>
            </Drawer>
        );
    }

    private _renderLinkText(linkText: string | undefined): JSX.Element {
        return <React.Fragment>{linkText}</React.Fragment>;
    }

    private _renderDrawerLink(item: IMenuItemData): JSX.Element | null {
        if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
            return this._renderLinkMenuItem(item);
        } else if (item && item.linkText && !item.linkURL) {
            return this._renderSpanMenuItem(item);
        }
        return null;
    }

    private _renderDisplay(): JSX.Element[] {
        const { ListItem, menuItemData, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (let i = 0; i < menuItemData.length; i++) {
                if (menuItemData[i] && menuItemData[i].id === activeMenu) {
                    menuItem = menuItemData[i];
                    this.setState({ parentMenu: undefined, mobileViewLabelText: menuItemData[i].linkText });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemData[i]) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._createMenuItemList(menuItem)}{' '}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem}>
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }

    private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    this.setState({ parentMenu: item && item.id, mobileViewLabelText: subMenus[i].linkText });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(menuItemData.subMenu, menuItemData.id);
            } else {
                return (
                    <React.Fragment>
                        {this._renderButtonMenuItem(menuItemData)}
                        {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
                    </React.Fragment>
                );
            }
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this._renderLinkMenuItem(menuItemData);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._renderSpanMenuItem(menuItemData);
        }

        return null;
    }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number): JSX.Element | null {
        const { isMobileView, ListItem } = this.props;

        if (!subMenus || subMenus.length === 0) {
            return null;
        }

        let levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;
                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                }

                let subMenu;

                if (hasOptions) {
                    this.currentLevel++;
                    if (this.currentLevel <= this.supportedLevel - 1) {
                        levelClassName = `level-${this.currentLevel.toString()}`;
                        subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined);
                    }
                }

                return (
                    <Node {...ListItem} key={option.id}>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });

        return this._renderMenu(levelClassName, menuOptions, activeMenu);
    }

    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <Node
                key={index}
                {...Button}
                onClick={this._handleDropdownToggle(option, activeMenu)}
                onFocus={this._closeSubmenu}
                aria-haspopup={true}
                aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    private _renderLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Link } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <Node
                {...Link}
                key={index}
                href={option.linkURL}
                {...attributes}
                onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
            >
                {option.linkText}
            </Node>
        );
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span}>
                {option.linkText}
            </Node>
        );
    }

    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null {
        const { DivContainer, MenuList, showCategoryImage } = this.props;
        this.currentLevel = 1;
        if (showCategoryImage) {
            return (
                <Node {...DivContainer} className={DivContainer!.className}>
                    <Node {...MenuList} className={classnames(MenuList.className, level)}>
                        {menuOptions}
                    </Node>
                </Node>
            );
        }
        return (
            <Node {...MenuList} className={classnames(MenuList.className, level)}>
                {menuOptions}
            </Node>
        );
    }

    private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
        } else {
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }

        this._resetFocus();
    };

    private _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    // tslint:disable-next-line:no-any
    private _handleClickOutside = (event: any) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    };

    private _closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    }
    private _escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode === this.escapeKey) {
            this._closeSubmenu();
        }
    };

    /////////////////////////////////////////////////
    /////// LAYOUT 2022 ////////////////////////////
    ////////////////////////////////////////////////

    /**
     * Method to render drawer menu.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _layout2022renderDrawerMenu(menuItem: IMenuItemData, setSize: number, posinset: number): JSX.Element | null {
        if (menuItem && menuItem.subMenu && ArrayExtensions.hasElements(menuItem.subMenu)) {
            return this._layout2022renderDrawer(menuItem, setSize, posinset, true);
        }
        return this._layout2022renderLinkMenuItem(menuItem, undefined, true, false, setSize, posinset);
    }

    /**
     * Method to render drawer component.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _layout2022renderDrawer(
        menuItem: IMenuItemData,
        setSize?: number,
        posinset?: number,
        firstLevel?: boolean
    ): JSX.Element | null {
        const toggleButtonText = menuItem.linkText;
        const keyValue = this.state.drawerKeyValue;
        const buttonText = toggleButtonText !== undefined ? toggleButtonText : '';
        const keys = keyValue !== undefined ? keyValue : {};
        const { isMobileView } = this.props;

        let isDrawerOpen = false;
        if (keys[menuItem?.id ? `${menuItem.id}_${buttonText}` : buttonText]) {
            isDrawerOpen = true;
        }
        const colProps: ICollapseProps = { isOpen: isDrawerOpen };

        const bottomLinkData: ICmsNavItemsData[] = firstLevel
            ? this.props.config.cmsNavItems?.filter(navItem => navItem.linkText === buttonText) || []
            : [];
        const bottomAllLink: IMenuItemData = {
            linkText: bottomLinkData[0]?.bottomLinkText || '',
            linkURL: bottomLinkData[0]?.bottomLinkUrl?.destinationUrl || '',
            shouldOpenNewTab: false
        };
        return (
            <Drawer
                role='listbox'
                toggleButtonProps={{
                    'aria-setsize': bottomAllLink.linkText === '' ? setSize : setSize! + 1,
                    'aria-posinset': posinset,
                    role: 'option'
                }}
                ariaLabel={isMobileView ? menuItem.ariaLabel : undefined}
                key={menuItem.id}
                className='ms-nav__drawer'
                openGlyph='ms-nav__drawer-open'
                onToggle={drawerState => this._layout2022onDrawerChange(drawerState, menuItem.id)}
                collapseProps={colProps}
                closeGlyph='ms-nav__drawer-close'
                glyphPlacement='end'
                toggleButtonText={this._layout2022renderLinkText(
                    toggleButtonText,
                    bottomAllLink.linkText === '' ? setSize : setSize! + 1,
                    posinset
                )}
            >
                <div role='listbox'>
                    {isMobileView && bottomAllLink.linkText !== '' ? (
                        <>
                            {this._layout2022renderDrawerLink(
                                bottomAllLink,
                                menuItem.subMenu!.length + 1,
                                menuItem.subMenu!.length + this._positionInSetOffset
                            )}
                            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, currentPos: number) => {
                                if (ArrayExtensions.hasElements(menuSubItem.subMenu)) {
                                    return this._layout2022renderDrawer(
                                        menuSubItem,
                                        menuItem.subMenu!.length + 1,
                                        currentPos + this._positionInSetOffset
                                    );
                                }
                                return this._layout2022renderDrawerLink(
                                    menuSubItem,
                                    menuItem.subMenu!.length + 1,
                                    currentPos + this._positionInSetOffset
                                );
                            })}
                        </>
                    ) : (
                        <>
                            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, currentPos: number) => {
                                if (ArrayExtensions.hasElements(menuSubItem.subMenu)) {
                                    return this._layout2022renderDrawer(
                                        menuSubItem,
                                        bottomAllLink.linkText === '' ? menuItem.subMenu?.length : menuItem.subMenu!.length + 1,
                                        currentPos + this._positionInSetOffset
                                    );
                                }
                                return this._layout2022renderDrawerLink(
                                    menuSubItem,
                                    bottomAllLink.linkText === '' ? menuItem.subMenu?.length : menuItem.subMenu!.length + 1,
                                    currentPos + this._positionInSetOffset
                                );
                            })}
                            {bottomAllLink.linkText === '' ? (
                                <></>
                            ) : (
                                this._layout2022renderDrawerLink(
                                    bottomAllLink,
                                    menuItem.subMenu!.length + 1,
                                    menuItem.subMenu!.length + this._positionInSetOffset
                                )
                            )}
                        </>
                    )}
                </div>
            </Drawer>
        );
    }

    /**
     * Method to render link.
     * @param linkText -Text on Menu link.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _layout2022renderLinkText(
        linkText: string | undefined,
        setSize: number | undefined,
        posinset: number | undefined
    ): JSX.Element {
        return (
            <span aria-setsize={setSize} aria-posinset={posinset}>
                {linkText}
            </span>
        );
    }

    /**
     * Method to render drawer link.
     * @param item -Single Menuitem.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _layout2022renderDrawerLink(item: IMenuItemData, setSize: number | undefined, posinset: number): JSX.Element | null {
        if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
            return this._layout2022renderLinkMenuItem(item, undefined, true, false, setSize, posinset);
        } else if (item && item.linkText && !item.linkURL) {
            return this._layout2022renderSpanMenuItem(item);
        }
        return null;
    }

    /**
     * Method to generate menu.
     * @returns Jsx element.
     */
    private _layout2022renderDisplay(): JSX.Element[] {
        const { ListItem, menuItemData, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (const menuItemDatum of menuItemData) {
                if (menuItemDatum && menuItemDatum.id === activeMenu) {
                    menuItem = menuItemDatum;
                    this.setState({ parentMenu: undefined });
                    break;
                }
                menuItem = this._layout2022getFromSubMenu(menuItemDatum) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._layout2022createMenuItemList(menuItem)}{' '}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem} data-active={activeMenu === item.id}>
                        {this._layout2022createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }

    /**
     * Method to get data for submenu.
     * @param item -Single Menuitem.
     * @returns IMenuItemData.
     */
    private _layout2022getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    this.setState({ parentMenu: item?.id });
                    return subMenus[i];
                }
                const found = this._layout2022getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    /**
     * Method to create item list.
     * @param menuItemData -Single Menuitem.
     * @returns Jsx element.
     */
    private _layout2022createMenuItemList(menuItemData: IMenuItemData, index?: number, level?: number): JSX.Element | null {
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return this._layout2022renderSubMenu(menuItemData.subMenu, menuItemData.id, undefined, menuItemData.linkText);
            }
            return (
                <>
                    {this._layout2022renderButtonMenuItem(menuItemData)}
                    {this._layout2022renderSubMenu(menuItemData.subMenu, menuItemData.id, undefined, menuItemData.linkText, true)}
                </>
            );
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this._layout2022renderLinkMenuItem(menuItemData, menuItemData.id, false, true);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._layout2022renderSpanMenuItem(menuItemData, menuItemData.id, true);
        }

        return null;
    }

    private _layout2022renderSubMenu(
        subMenus?: IMenuItemData[],
        activeMenu?: number,
        IsSubMenu?: boolean,
        nameParentElement?: string,
        firstLevel?: boolean
    ): JSX.Element | null {
        const { isMobileView, ListItem } = this.props;
        const enableMultiSupportMenu = this.props.config.enableMultilevelMenu || false;
        const subMenuLevel = 3;
        const multiLevelSupportedMenu = this.props.config.menuLevelSupport || subMenuLevel;

        // Const isParentMenu:boolean= false;
        if (activeMenu && this.state.activeMenu !== activeMenu) {
            //this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            //this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }

        let levelClassName: string = '';

        const bottomLinkData: ICmsNavItemsData[] = nameParentElement
            ? this.props.config.cmsNavItems?.filter(navItem => navItem.linkText === nameParentElement) || []
            : [];
        const bottomLinkDataFiltered: ICmsNavItemsData[] = bottomLinkData.filter(navItem => navItem.bottomLinkText != undefined) || [];
        const bottomLinkPresent: boolean = (firstLevel && bottomLinkDataFiltered.length > 0) || false;
        const bottomMenuLink: JSX.Element | null = bottomLinkPresent
            ? this._layout2022renderLinkMenuItem({
                  linkText: bottomLinkData[0]?.bottomLinkText || '',
                  linkURL: bottomLinkData[0]?.bottomLinkUrl?.destinationUrl || '',
                  shouldOpenNewTab: false
              })
            : null;
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;

                let menuItem: JSX.Element | null;

                if (hasOptions && isMobileView) {
                    menuItem = this._layout2022renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL
                        ? this._layout2022renderLinkMenuItem(option, idx)
                        : this._layout2022renderSpanMenuItem(option);
                }

                let subMenu;

                const haveSubmenu = hasOptions && enableMultiSupportMenu && this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
                if (haveSubmenu) {
                    this.currentLevel++;
                    levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                    subMenu = this._layout2022renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true);
                }
                return (
                    <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });
        return this._layout2022renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu, bottomLinkPresent, bottomMenuLink);
    }

    /**
     * Method to render button menu item.
     * @param option -Single Menuitem.
     * @param activeMenu -Active menu number.
     * @param index -Active menu index.
     * @returns Jsx element.
     */
    private _layout2022renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <Node
                key={index}
                {...Button}
                onClick={this._layout2022handleDropdownToggle(option, activeMenu)}
                onFocus={this._layout2022closeSubmenu}
                aria-haspopup={!(this.state.activeMenu && this.state.activeMenu === option.id)}
                aria-expanded={!!(this.state.activeMenu && this.state.activeMenu === option.id)}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to find an item in menu items
     * @param menu
     * @param itemId
     * @param parents
     * @returns
     */
    private findItemAndParents(menuItems: IMenuItemData[], itemId: number | undefined, parents: string[]): MenuSearchResult {
        if (!itemId || !menuItems.length) return null;

        for (let item of menuItems) {
            if (item.id === itemId && item.subMenu?.length === 0) {
                return { item, parents };
            }

            if (item.subMenu && item.subMenu.length > 0) {
                const result = this.findItemAndParents(item.subMenu, itemId, [...parents, item.linkText || '']);

                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    /**
     * Method to render link menu item.
     * @param option -Single Menuitem.
     * @param index -Active menu index.
     * @param hoverEffect -Active menu effect.
     * @param isParent -Is parent menu.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private sendEventGA(option: IMenuItemData, isMobileView: boolean) {
        // New Approach
        const clickedItem = this.findItemAndParents(this.props.menuItemData, option.id, []);

        if (clickedItem) {
            trackClickMenu(clickedItem.parents[0] || '', clickedItem.parents[1] || '', clickedItem.item.linkText || '');
        }
    }

    private _layout2022renderLinkMenuItem(
        option: IMenuItemData,
        index?: number,
        hoverEffect: boolean = true,
        isParent: boolean = false,
        setSize?: number,
        posinset?: number
    ): JSX.Element | null {
        const { Link, isMobileView } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <Node
                aria-setsize={isMobileView ? setSize : undefined}
                aria-posinset={isMobileView ? posinset : undefined}
                aria-label={isMobileView ? option.ariaLabel : undefined}
                {...Link}
                key={index}
                onFocus={isParent ? this._layout2022closeSubmenu : undefined}
                target={option.shouldOpenNewTab ? '_blank' : undefined}
                href={option.linkURL}
                {...attributes}
                onClick={() => {
                    this.sendEventGA(option, isMobileView);
                    onTelemetryClick(this.telemetryContent, this.payLoad, linkText);
                }}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render promotional link.
     * @param linkText -Link text.
     * @param linkUrl -Link url.
     * @returns Jsx element.
     */
    private _layout2022renderPromotionalLink(linkText?: string, linkUrl?: string): JSX.Element | null {
        const { Link } = this.props;
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (linkText && linkUrl) {
            return (
                <Node {...Link} href={linkUrl} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}>
                    {linkText}
                </Node>
            );
        }
        return null;
    }

    /**
     * Method to render span menu item.
     * @param option -Single Menu Item.
     * @param index -Index.
     * @param isParent -Is parent menu.
     * @returns Jsx element.
     */
    private _layout2022renderSpanMenuItem(option: IMenuItemData, index?: number, isParent: boolean = false): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span} onFocus={isParent ? this._layout2022closeSubmenu : undefined}>
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render menu.
     * @param level -Menu level.
     * @param menuOptions -Menu lists.
     * @param currentItem -Menu current.
     * @param submenu -Mneu has submenu or not.
     * @returns Jsx element.
     */
    private _layout2022renderMenu(
        level: string,
        menuOptions: JSX.Element[],
        currentItem?: number,
        submenu?: boolean,
        bottomLinkAvail?: boolean,
        bottomLink?: JSX.Element | null
    ): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage, isMobileView, showPromotionalContent } = this.props;
        const categoryImageDisplay =
            !isMobileView && showCategoryImage && this.state.categoryImage !== null && this.state.categoryImage !== undefined && !submenu;
        const promotionalContentDisplay =
            !isMobileView && showPromotionalContent && ArrayExtensions.hasElements(this.state.categoryImage) && !submenu;
        const DivContainerClass =
            this.currentLevel > NavMenuConstants.two || categoryImageDisplay ? DivContainer!.className : 'ms-nav__deafult';
        this.currentLevel = NavMenuConstants.one;
        const categoryImages = this.state.categoryImage?.filter(image => !StringExtensions.isNullOrWhitespace(image.src));
        const imagesContainerKey = categoryImages?.map(item => item.src).join('-');

        return (
            <Node {...DivContainer} className={DivContainerClass}>
                <Node
                    {...MenuList}
                    className={classnames(
                        MenuList.className,
                        level,
                        categoryImageDisplay && 'havecateImage',
                        categoryImageDisplay &&
                            this.props.config.menuLevelSupport &&
                            this.props.config.menuLevelSupport > 2 &&
                            'navmenu-multi-level'
                    )}
                >
                    {menuOptions}
                </Node>
                <div className='category-image-container' key={imagesContainerKey}>
                    {categoryImageDisplay &&
                        ArrayExtensions.hasElements(categoryImages) &&
                        categoryImages.map(item => (
                            <Node {...ImageDivContainer} className={ImageDivContainer!.className} key={item.src}>
                                {getCategoryImage(this.props, item.src ?? '', item.altText!.toString())}
                                {promotionalContentDisplay &&
                                    this._layout2022renderPromotionalLink(item.altText, item.additionalProperties?.linkUrl)}
                            </Node>
                        ))}
                </div>
                {bottomLinkAvail ? (
                    <>
                        <div className='flex-rows-break'>
                            <div className='flex-rows-break-line'></div>
                        </div>
                        <div className='bottomlink-menu-section'>{bottomLink}</div>
                    </>
                ) : (
                    <> </>
                )}
            </Node>
        );
    }

    /**
     * Method to handle dropdwon change.
     * @param data -Menuitem data.
     * @param parentId -Menu parent.
     * @returns Jsx element.
     */
    private readonly _layout2022handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
            if (this.props.showCategoryImage) {
                this._layout2022updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            this.setState({
                activeMenu: data.id,
                parentMenu: parentId
            });
        }

        this._layout2022resetFocus();
    };

    /**
     * Method to call when focus lost from menu.
     */
    private readonly _layout2022resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _layout2022handleClickOutside = (event: MouseEvent) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target as Node)) {
            this.setState({
                activeMenu: undefined,
                categoryImage: null
            });
        }
    };

    /**
     * Method to handle close submenu event.
     */
    private _layout2022closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined });
        }
    }

    /**
     * Method to handle escape key event.
     * @param event -HTML event.
     */
    private readonly _layout2022escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode === this.escapeKey) {
            if (!this.props.isMobileView) {
                const navDrawerList = Array.from(this.menuNode.current!.childNodes);
                for (const item of navDrawerList) {
                    const navDrawerButton = item.firstChild as HTMLButtonElement;
                    if (navDrawerButton.getAttribute('aria-expanded') === 'true') {
                        navDrawerButton.focus();
                    }
                }
            }
            this._layout2022closeSubmenu();
        }
    };

    /**
     * Method to handle escape key event.
     * @param drawerState -HTML event.
     */
    private readonly _layout2022onDrawerChange = (drawerState: IDrawerState, menuItemId?: number) => {
        const { drawerKeyValue } = this.state;
        const drawerId = menuItemId !== undefined ? String(menuItemId) : '';
        const drawerText: string = drawerState.buttonText !== undefined ? drawerState.buttonText : '';
        const uniqueKey = drawerId ? `${drawerId}_${drawerText}` : drawerText;
        const newPair = { [uniqueKey]: drawerState.isOpen };
        this.setState({ drawerKeyValue: { ...drawerKeyValue, ...newPair } });
    };

    /**
     * Handle Category image update.
     * @param categoryImageSource - Image source.
     * @param option - Menu Item data.
     * @returns Returns empty.
     */
    private readonly _layout2022updateCategoryImage = (categoryImageSource: string, option: IMenuItemData) => () => {
        const linkText = option.linkText ? option.linkText : '';
        const promotionalImage: IImageData[] = [{ src: categoryImageSource, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            for (const item of option.promotionalContent) {
                const imageSource = item.image.src;
                promotionalImage.push({
                    src: imageSource,
                    altText: item.text,
                    additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                });
            }
        }
        this.setState({
            categoryImage: ArrayExtensions.hasElements(promotionalImage) ? promotionalImage : [{ src: 'empty' }]
        });
    };

    ////////////////////////////////////////
    ////////// CLASS///////////////////////
    //////////////////////////////////
}
export default NavigationMenuView;
